import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search,
  Phone,
  Eye,
  Download,
  X,
  FileText,
  RefreshCw
} from "lucide-react";
import { renewalsAPI } from "../../services/api";

// Helper function to format date from YYYY-MM-DD to DD/M/YYYY
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export function RenewList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState({ fileName: "", memberName: "" });
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchRenewals();
  }, [page, limit]);

  const fetchRenewals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { page, limit };
      
      const response = await renewalsAPI.getAll(params);
      
      if (response.success) {
        setRenewals(response.data || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 0);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch renewals");
      console.error("Error fetching renewals:", err);
    } finally {
      setLoading(false);
    }
  };

const filteredRenewals = renewals.filter(renewal => {
    const memberName = renewal.member_name || "";
    const memberNumber = renewal.member_number || "";
    const renewalNumber = renewal.renewal_number || "";
    
    const matchesSearch = 
      memberName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      memberNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      renewalNumber.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const handleViewDetails = (renewal) => {
    navigate(`/renew/${renewal.id}`, { state: { renewal } });
  };

  const handleViewDocument = (filePath, memberName) => {
    setSelectedDocument({ filePath, memberName });
    setShowDocumentModal(true);
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument({ fileName: "", memberName: "" });
  };

  const handleDownloadDocument = async (filePath) => {
    if (!filePath) return;
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://backend.aifia.in';
      const response = await fetch(`${apiBaseUrl}/${filePath}`);
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filePath.split('/').pop() || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      window.open(`${process.env.REACT_APP_API_BASE_URL || 'https://backend.aifia.in'}/${filePath}`, '_blank');
    }
  };

  // Sort renewals by date (newest first)
  const sortedRenewals = [...filteredRenewals].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Renewals</h1>
          <p className="text-gray-600">Manage membership renewals</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-[#C46A1B] mx-auto mb-2" />
            <p className="text-gray-500">Loading renewals...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Renewals</h1>
          <p className="text-gray-600">Manage membership renewals</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchRenewals}
            className="mt-2 px-4 py-2 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Renewals</h1>
          <p className="text-gray-600">Manage membership renewals</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm flex flex-wrap gap-4">
        <div className="flex-1 flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
          <Search className="w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, ID, or renewal number..."
            className="bg-transparent border-none outline-none text-sm w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Renewals Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm text-gray-600">
                <th className="p-4">Date</th>
                <th className="p-4">Renewal No.</th>
                <th className="p-4">Member Name</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Details</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedRenewals.map((renewal) => (
                <tr key={renewal.id} className="hover:bg-gray-50">
                  <td className="p-4 text-sm whitespace-nowrap">
                    {formatDate(renewal.renewal_date)}
                  </td>
                  <td className="p-4 font-mono text-sm font-medium">
                    {renewal.renewal_number || "N/A"}
                  </td>
                  <td className="p-4 font-medium">{renewal.member_name || "N/A"}</td>
                 
                  <td className="p-4">
                    <div className="flex flex-col text-sm">
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3 text-gray-400" /> {renewal.member_phone || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => handleViewDetails(renewal)}
                      className="flex items-center gap-1 text-[#C46A1B] hover:text-[#9E4E10] text-sm font-medium"
                      title="View renewal details"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {/* View Document */}
                      {renewal.artist_card_path && (
                        <>
                          <button
                            onClick={() => handleViewDocument(renewal.artist_card_path, renewal.member_name)}
                            className="p-1 hover:bg-blue-50 rounded transition-colors"
                            title="View Artist Card"
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </button>

                          {/* Download Document */}
                          <button
                            onClick={() => handleDownloadDocument(renewal.artist_card_path)}
                            className="p-1 hover:bg-green-50 rounded transition-colors"
                            title="Download Artist Card"
                          >
                            <Download className="w-4 h-4 text-green-600" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {sortedRenewals.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <p>No renewals found</p>
          </div>
        )}
        
        {/* Pagination */}
        <div className="p-4 border-t flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing {sortedRenewals.length} of {total} renewals
          </p>
          <div className="flex gap-2">
            <button 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button className="px-3 py-1 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]">
              {page}
            </button>
            <button 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Document View Modal */}
      {showDocumentModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 0,
            margin: 0
          }}
          onClick={handleCloseDocumentModal}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C46A1B]/10 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#C46A1B]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#111111]">
                    Artist Card - {selectedDocument.memberName}
                  </h3>
                </div>
              </div>
              <button
                onClick={handleCloseDocumentModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
              <div className="bg-gray-100 rounded-xl p-0 flex flex-col items-center justify-center min-h-[0px] border-2 border-dashed border-gray-300">
                {selectedDocument.filePath?.toLowerCase().includes('.pdf') ? (
                  <>
                    <FileText className="w-20 h-20 text-gray-400 mb-4" />
                    <p className="text-gray-500 text-center mb-2">PDF Document</p>
                    <a
                      href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${selectedDocument.filePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#C46A1B] hover:underline"
                    >
                      Open PDF in new tab
                    </a>
                  </>
                ) : (
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${selectedDocument.filePath}`}
                    alt="Document Preview"
                    className="max-w-full max-h-[400px] object-contain rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/400x300?text=No+Preview";
                    }}
                  />
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleCloseDocumentModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleDownloadDocument(selectedDocument.filePath);
                    handleCloseDocumentModal();
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#C46A1B] text-white rounded-lg hover:bg-[#9E4E10] transition"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
